import type { ResubmitBlocker } from './reviewGates';
import { taskCommentFieldAnchorKey } from '../../utils/taskCommentAnchors';
import type { TaskCommentFieldAnchor } from '../../types';

/**
 * Human-readable names for the field paths the resubmit gate reports.
 *
 * Task views label their fields through `FieldCommentSlot`'s `label` prop;
 * the shell collects those into a `fieldPath → label` map so footer copy can
 * say "Performance and revisions" instead of `performanceAndRevisions`. Paths
 * with no registered label fall back to a humanized version of the path, so
 * the footer never shows raw identifiers even for modules that skip labels or
 * keep the field on a tab that is not mounted right now.
 */

const TASK_LEVEL_LABEL = 'the task';

function humanizeSegment(segment: string): string {
  if (/^\d+$/.test(segment)) return `#${Number(segment) + 1}`;
  const words = segment
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .trim()
    .toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

export function describeFieldPath(
  fieldPath: string | null,
  labels: ReadonlyMap<string, string> = new Map(),
): string {
  if (!fieldPath) return TASK_LEVEL_LABEL;
  const exact = labels.get(fieldPath);
  if (exact) return exact;
  const segments = fieldPath.split('.');
  // Prefer the closest labelled ancestor so `toolCalls.0.name` reads as
  // "Tool calls › #1 › Name" when only the list slot carries a label.
  for (let end = segments.length - 1; end > 0; end -= 1) {
    const ancestor = labels.get(segments.slice(0, end).join('.'));
    if (ancestor) {
      return [ancestor, ...segments.slice(end).map(humanizeSegment)].join(' › ');
    }
  }
  return segments.map(humanizeSegment).join(' › ');
}

export interface BlockedFieldDescription {
  fieldPath: string | null;
  fieldAnchor?: TaskCommentFieldAnchor;
  label: string;
}

function describeAnchor(anchor: TaskCommentFieldAnchor): string {
  if (anchor.type === 'rubric-grade') {
    return `Grade for ${anchor.modelKey} requirement`;
  }
  const field = humanizeSegment(anchor.field);
  return `Rubric requirement ${field.toLowerCase()}`;
}

/** One entry per distinct field, in first-seen order, each with its display label. */
export function describeBlockedFields(
  blockedBy: readonly ResubmitBlocker[],
  labels?: ReadonlyMap<string, string>,
): BlockedFieldDescription[] {
  const seen = new Set<string>();
  const fields: BlockedFieldDescription[] = [];
  for (const blocker of blockedBy) {
    const key = taskCommentFieldAnchorKey(blocker.fieldAnchor)
      ?? blocker.fieldPath
      ?? '__task__';
    if (seen.has(key)) continue;
    seen.add(key);
    fields.push({
      fieldPath: blocker.fieldPath,
      fieldAnchor: blocker.fieldAnchor,
      label: blocker.fieldPath
        ? describeFieldPath(blocker.fieldPath, labels)
        : blocker.fieldAnchor
          ? `${describeAnchor(blocker.fieldAnchor)}${blocker.fieldAnchorState === 'orphaned' ? ' (needs reassignment)' : ''}`
          : TASK_LEVEL_LABEL,
    });
  }
  return fields;
}
